import React, { useState, useEffect } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { Link } from 'react-router-dom';
import { blogService } from '../services/blogService';
import { Calendar, Clock } from 'lucide-react';

const Blogs = () => {
    const [blogs, setBlogs] = useState([]);
    const [pagination, setPagination] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchBlogs();
        AOS.init({
            duration: 1000,
            once: true,
            mirror: false,
        });
    }, []);

    const fetchBlogs = async (page = 1) => {
        try {
            setLoading(true);
            const response = await blogService.getAll({ page });

            // Combine featured post and other posts into one array
            const allPosts = [];
            // Only add featured post on first page
            if (response.data.featuredPost && page === 1) {
                allPosts.push(response.data.featuredPost);
            }
            if (response.data.posts.data) {
                allPosts.push(...response.data.posts.data);
            }

            setBlogs(allPosts);
            setPagination(response.data.posts);
        } catch (err) {
            console.error('Error fetching blogs:', err);
            setError('Failed to load blogs. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const stripHtmlTags = (html) => {
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || '';
    };

    const getExcerpt = (content) => {
        const plainText = stripHtmlTags(content);
        return plainText.length > 150
            ? plainText.substring(0, 150) + '...'
            : plainText;
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-white pt-32 pb-20">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <p className="text-xl" style={{ color: '#31415A' }}>Loading blogs...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-white pt-32 pb-20">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <p className="text-xl text-red-600">{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 pt-32 pb-20">
            <div className="max-w-5xl mx-auto px-6 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-12" data-aos="fade-up">
                    <h1 className="text-3xl md:text-5xl font-bold mb-2" style={{ color: '#31415A' }}>
                        Our Blog
                    </h1>
                    <p className="text-lg text-gray-600">
                        Coffee insights, brewing tips, and updates from Iraga
                    </p>
                </div>

                {/* Blog List */}
                {blogs.length > 0 ? (
                    <div className="space-y-6">
                        {blogs.map((blog, index) => (
                            <Link
                                key={blog.id}
                                to={`/blogs/${blog.slug}`}
                                className="group flex flex-col md:flex-row gap-6 p-6 bg-white border border-gray-200 rounded-2xl hover:border-[#C9A14F]/50 hover:shadow-lg transition-all"
                                data-aos="fade-up"
                                data-aos-delay={index * 100}
                            >
                                {/* Thumbnail */}
                                <div className="flex-shrink-0 w-full md:w-72 h-48 md:h-48 bg-gray-100 rounded-xl overflow-hidden">
                                    <img
                                        src={blog.image || '/images/placeholder-blog.jpg'}
                                        alt={blog.title}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                        onError={(e) => {
                                            e.target.src = '/images/placeholder-blog.jpg';
                                        }}
                                    />
                                </div>

                                {/* Content */}
                                <div className="flex-1 flex flex-col justify-between py-2">
                                    <div>
                                        {/* Featured Badge */}
                                        {index === 0 && (
                                            <span
                                                className="inline-block px-3 py-1 text-xs font-bold rounded-full mb-3"
                                                style={{ backgroundColor: '#C9A14F', color: 'white' }}
                                            >
                                                FEATURED
                                            </span>
                                        )}

                                        {/* Title */}
                                        <h2
                                            className="text-2xl font-bold mb-3 group-hover:opacity-75 transition-opacity line-clamp-2"
                                            style={{ color: '#31415A' }}
                                        >
                                            {blog.title}
                                        </h2>

                                        {/* Excerpt */}
                                        <p className="text-gray-600 mb-4 line-clamp-2">
                                            {getExcerpt(blog.content || '')}
                                        </p>
                                    </div>

                                    {/* Meta Info */}
                                    <div className="flex items-center gap-4 text-sm text-gray-500">
                                        <div className="flex items-center gap-1.5">
                                            <Calendar className="w-4 h-4" />
                                            <span>
                                                {new Date(blog.published_at).toLocaleDateString('en-US', {
                                                    year: 'numeric',
                                                    month: 'short',
                                                    day: 'numeric'
                                                })}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <Clock className="w-4 h-4" />
                                            <span>5 min read</span>
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-20">
                        <div className="mb-4">
                            <svg
                                className="w-16 h-16 mx-auto text-gray-300"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
                                />
                            </svg>
                        </div>
                        <p className="text-xl text-gray-500">No blogs available at the moment.</p>
                        <p className="text-sm text-gray-400 mt-2">Check back soon for new stories!</p>
                    </div>
                )}

                {/* Pagination */}
                {pagination.last_page > 1 && (
                    <div className="mt-12 flex justify-center gap-2">
                        <button
                            onClick={() => fetchBlogs(pagination.current_page - 1)}
                            disabled={pagination.current_page === 1}
                            className="px-4 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            Previous
                        </button>

                        {Array.from({ length: pagination.last_page }, (_, i) => i + 1).map((page) => (
                            <button
                                key={page}
                                onClick={() => fetchBlogs(page)}
                                className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${pagination.current_page === page
                                    ? 'bg-[#31415A] text-white'
                                    : 'border border-gray-200 hover:bg-gray-50'
                                    }`}
                            >
                                {page}
                            </button>
                        ))}

                        <button
                            onClick={() => fetchBlogs(pagination.current_page + 1)}
                            disabled={pagination.current_page === pagination.last_page}
                            className="px-4 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            Next
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Blogs;
